import {getApp} from '@firebase/app';
import {User, getAuth} from '@firebase/auth';
import {off as dbRefOff, getDatabase, ref as getDatabaseRef, onValue} from 'firebase/database';
import React from 'react';
import {useStateWithDeps} from 'use-state-with-deps';

import {LOGIN_APP_URL, setWindowLocationHref, startSignOutHandoff} from 'src/lib/routeUtils';

/**
 * Maintains the current user's "idToken", aka JWT token for API authentication.
 * Though this is always accessible from the getIdToken() method on
 * firebase.auth.User, we keep the latest in state so we can provide it
 * synchronously for APIs that couldn't deal with getIdToken's Promise return
 * value. (Such as Mapbox's transformRequest)
 */
export default function useFirebaseIdToken(firebaseUser: User | null | undefined) {
  const [idToken, setIdToken] = useStateWithDeps<string | null>(null, [
    firebaseUser && firebaseUser.uid,
  ]);

  React.useEffect(() => {
    if (!firebaseUser) {
      setIdToken(null);
      return;
    }

    // This will "immediately" return a value (after promise resolution) on
    // subscribe, as well as call us back when there are changes. We don't have
    // to proactively keep refreshing when the idTokens expire (they last an
    // hour) because we rely on our connection to the realtime database to do
    // that for us.
    const app = getApp();
    const auth = getAuth(app);
    return auth.onIdTokenChanged(async (currentUser) => {
      if (currentUser && currentUser.uid === firebaseUser.uid) {
        setIdToken(await currentUser.getIdToken());
      }
    });
  }, [firebaseUser, setIdToken]);

  const lastProfileUpdateRef = React.useRef<string | null>(null);

  // Listen for profile changes in the realtime database. When the backend
  // revokes a user's refresh token (credentials reset / force logout), the
  // next getIdToken(true) call throws auth/user-token-expired. We use that to
  // kick the user out immediately rather than waiting for the 1-hour ID token
  // expiry.
  React.useEffect(() => {
    if (!firebaseUser) {
      lastProfileUpdateRef.current = null;
      return;
    }

    const app = getApp();
    const auth = getAuth(app);
    const database = getDatabase(app);
    const profileRef = getDatabaseRef(database, `realtime/users/${firebaseUser.uid}/profile`);

    const listener = onValue(profileRef, async (snapshot) => {
      const updateVal: string | null = snapshot.val();
      if (updateVal === lastProfileUpdateRef.current) return;

      try {
        await firebaseUser.getIdToken(true);
      } catch (e) {
        const err = e as {code?: string};
        if (err.code === 'auth/user-token-expired') {
          // The token was revoked, so this is a sign-out: claim the navigation before
          // clearing the session, or Auth.tsx's redirect to the login app's root races it
          // and the app switcher hands the user back here.
          startSignOutHandoff();
          await auth.signOut();
          setWindowLocationHref(`${LOGIN_APP_URL}/signOut`);
          return;
        }
      }

      lastProfileUpdateRef.current = updateVal;
    });

    return () => {
      dbRefOff(profileRef, 'value', listener);
    };
  }, [firebaseUser]);

  return idToken;
}
